import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Users, Target, Lightbulb } from 'lucide-react';

const About = () => {

  return (
    <div className="pt-16">
      {/* Hero Section */}
      <section className="relative py-20 bg-gradient-to-br from-primary to-primary/90">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <h1 className="text-4xl md:text-5xl font-light text-text mb-8 text-center">
            AI That Actually <span className="text-secondary">Works</span>
          </h1>
          <div className="max-w-4xl mx-auto space-y-6 text-lg text-muted leading-relaxed">
            <p>
              Founded by an ML engineer with <strong className="text-text">7+ years deploying production systems at scale</strong>, recalibr.ai makes AI implementation accessible for businesses ready to move fast.
            </p>
            <p>
              In an era defined by rapid technological acceleration, the true competitive edge lies not in adopting every new technology: it's choosing the <em>right one</em>, implementing it the <em>right way</em>, at the <em>right time</em>.
            </p>
            <p>
              We're an AI implementation studio that builds <strong className="text-text">production-ready systems fast</strong>, with a relentless focus on outcomes that matter: faster decisions, lower costs, better customer experiences.
            </p>
            <p>
              We work at the intersection of business and technology — understanding your workflows deeply enough to build AI that <em>fits naturally, not awkwardly</em>. No endless POCs. No buzzword bingo. Just <strong className="text-text">working systems that deliver measurable value</strong>.
            </p>
          </div>
        </div>
      </section>

      {/* Philosophy Section */}
      <section className="py-20 bg-primary">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="max-w-3xl mx-auto">
            <h2 className="text-3xl md:text-4xl font-light text-text mb-8 text-center">
              Our Philosophy
            </h2>
            <p className="text-lg text-muted leading-relaxed mb-8 text-center">
              AI should amplify human intelligence, not replace it. We see technology as a tool to enhance focus, reduce friction, and unlock creativity.
            </p>
            <div className="grid gap-8 md:grid-cols-3 mt-12">
              <div>
                <h3 className="text-xl font-semibold text-white mb-3">Relevance over Hype</h3>
                <p className="text-muted">We focus on what drives measurable outcomes, not what's trending.</p>
              </div>
              <div>
                <h3 className="text-xl font-semibold text-white mb-3">Simplicity through Design</h3>
                <p className="text-muted">The best AI systems feel invisible — they integrate naturally into existing workflows.</p>
              </div>
              <div>
                <h3 className="text-xl font-semibold text-white mb-3">Ethics and Empathy</h3>
                <p className="text-muted">We build with transparency and trust so users feel empowered, not displaced.</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* What We've Built Section */}
      <section className="py-20 bg-gradient-to-b from-primary to-primary/90">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <h2 className="text-3xl md:text-4xl font-light text-text mb-12 text-center">
            What We've Built
          </h2>
          <div className="grid gap-8 md:grid-cols-3 max-w-6xl mx-auto">
            <div className="p-6 bg-gray-800 rounded-lg border border-secondary/20">
              <h3 className="text-xl font-semibold text-white mb-3">Procurement Automation</h3>
              <p className="text-muted text-sm mb-2">Real Estate</p>
              <p className="text-muted leading-relaxed">
                End-to-end automation of techno-commercial evaluation in procurement. Combines rule-based and LLM evaluation to help executives make faster, better-informed decisions on business-critical purchases.
              </p>
            </div>
            <div className="p-6 bg-gray-800 rounded-lg border border-secondary/20">
              <h3 className="text-xl font-semibold text-white mb-3">Cost Estimation AI</h3>
              <p className="text-muted text-sm mb-2">Manufacturing</p>
              <p className="text-muted leading-relaxed">
                AI model that estimates costs for custom decorative lighting fixtures — enabling faster turnaround on quotes without waiting for factory pricing. Speeds up sales cycles and improves customer experience.
              </p>
            </div>
            <div className="p-6 bg-gray-800 rounded-lg border border-secondary/20">
              <h3 className="text-xl font-semibold text-white mb-3">Synthetic Dataset Generation</h3>
              <p className="text-muted text-sm mb-2">Food Delivery</p>
              <p className="text-muted leading-relaxed">
                Built an open-source synthetic dataset for training food delivery search models — similar to Amazon's ESCI dataset but tailored for food queries across platforms like Zomato, Swiggy, and DoorDash.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* How We Work Section */}
      <section className="py-20 bg-primary">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <h2 className="text-3xl md:text-4xl font-light text-text mb-12 text-center">
            How We Work
          </h2>
          <div className="max-w-4xl mx-auto">
            <div className="grid gap-8 md:grid-cols-4">
              <div className="text-center">
                <div className="w-16 h-16 rounded-full bg-secondary/20 flex items-center justify-center mx-auto mb-4">
                  <span className="text-2xl font-bold text-secondary">1</span>
                </div>
                <h3 className="text-xl font-semibold text-white mb-2">Evaluate</h3>
                <p className="text-muted text-sm">Assess workflows and identify opportunities</p>
              </div>
              <div className="text-center">
                <div className="w-16 h-16 rounded-full bg-secondary/20 flex items-center justify-center mx-auto mb-4">
                  <span className="text-2xl font-bold text-secondary">2</span>
                </div>
                <h3 className="text-xl font-semibold text-white mb-2">Strategize</h3>
                <p className="text-muted text-sm">Design AI roadmaps grounded in business reality</p>
              </div>
              <div className="text-center">
                <div className="w-16 h-16 rounded-full bg-secondary/20 flex items-center justify-center mx-auto mb-4">
                  <span className="text-2xl font-bold text-secondary">3</span>
                </div>
                <h3 className="text-xl font-semibold text-white mb-2">Implement</h3>
                <p className="text-muted text-sm">Build and integrate intelligent systems</p>
              </div>
              <div className="text-center">
                <div className="w-16 h-16 rounded-full bg-secondary/20 flex items-center justify-center mx-auto mb-4">
                  <span className="text-2xl font-bold text-secondary">4</span>
                </div>
                <h3 className="text-xl font-semibold text-white mb-2">Evolve</h3>
                <p className="text-muted text-sm">Track, retrain, and refine as you grow</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Why Recalibr Section */}
      <section className="py-20 bg-gradient-to-b from-primary to-primary/90">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-light text-text mb-4">
              Why Work With Us
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-4xl mx-auto">
            <div className="p-6 bg-gradient-to-br from-secondary/10 to-transparent border border-secondary/20 rounded-lg hover:border-secondary/40 transition-all duration-300">
              <Target className="h-12 w-12 text-secondary mb-4" />
              <h3 className="text-xl font-medium text-text mb-3">
                Speed
              </h3>
              <p className="text-muted leading-relaxed">
                We move fast. AI-assisted development, lean processes, and direct communication — no layers of bureaucracy slowing things down.
              </p>
            </div>
            <div className="p-6 bg-gradient-to-br from-secondary/10 to-transparent border border-secondary/20 rounded-lg hover:border-secondary/40 transition-all duration-300">
              <Users className="h-12 w-12 text-secondary mb-4" />
              <h3 className="text-xl font-medium text-text mb-3">
                Personal Touch
              </h3>
              <p className="text-muted leading-relaxed">
                You work directly with experienced practitioners, not junior teams. We understand your business because we take the time to listen.
              </p>
            </div>
            <div className="p-6 bg-gradient-to-br from-secondary/10 to-transparent border border-secondary/20 rounded-lg hover:border-secondary/40 transition-all duration-300">
              <Lightbulb className="h-12 w-12 text-secondary mb-4" />
              <h3 className="text-xl font-medium text-text mb-3">
                Outcome-Focused
              </h3>
              <p className="text-muted leading-relaxed">
                No buzzwords. No vanity metrics. We focus on business outcomes that matter — faster decisions, cost savings, better customer experiences.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-r from-secondary to-secondary/80">
        <div className="max-w-4xl mx-auto px-6 lg:px-8 text-center">
          <h2 className="text-3xl md:text-4xl font-light text-white mb-4">
            Ready to Build Something Real?
          </h2>
          <p className="text-xl text-white/90 mb-8">
            Let's talk about what AI can actually do for your business — no sales pitch, just an honest conversation about what's possible.
          </p>
          <Link
            to="/contact"
            className="px-8 py-4 bg-gray-900 text-white rounded-lg hover:bg-gray-800 transition-colors duration-200 font-medium inline-flex items-center gap-2"
          >
            Book a Free Consultation!
            <ArrowRight size={20} />
          </Link>
        </div>
      </section>
    </div>
  );
};

export default About;
